import React, { useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';
import Pagination from '../components/Pagination';

export default function PostModeration() {
  useAuthGuard();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [cursorHistory, setCursorHistory] = useState([]);
  const [moderating, setModerating] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        setLoading(true);
        setError('');
        const url = new URL(`${API_BASE_URL}/admin/posts/pending`);
        url.searchParams.set('limit', '20');
        if (cursor) url.searchParams.set('cursor', cursor);

        const res = await apiFetch(url.toString(), { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const list = data.data?.items || data.items || data.data || [];
        setItems(Array.isArray(list) ? list : []);
        setHasMore(data.data?.has_more ?? data.has_more ?? false);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Network Error');
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [cursor]);

  const handleModerate = async (postId, status, reason = '') => {
    setModerating(postId);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/posts/${postId}/moderate`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, reason }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setItems((prev) => prev.filter((p) => (p.id || p.post_id) !== postId));
    } catch (e) {
      alert('Moderation failed: ' + e.message);
    } finally {
      setModerating(null);
    }
  };

  const handleNext = () => {
    if (items.length > 0) {
      const lastItem = items[items.length - 1];
      setCursorHistory((prev) => [...prev, cursor]);
      setCursor(lastItem.id || lastItem.post_id || '');
    }
  };

  const handlePrev = () => {
    setCursorHistory((prev) => {
      const next = [...prev];
      const prevCursor = next.pop();
      setCursor(prevCursor || '');
      return next;
    });
  };

  const fmtDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (isNaN(d)) return s;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  };

  return (
    <Layout>
      <div className="mb-4">
        <h2 className="page-title">Post Moderation</h2>
        <p className="page-subtitle">Review / moderate pending posts</p>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>User</th>
                <th>Content</th>
                <th>Store</th>
                <th>Created</th>
                <th style={{ width: '180px' }}>Operation</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="text-center py-4">Loading...</td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td colSpan={5} className="text-danger py-4">{error}</td>
                </tr>
              )}
              {!loading && !error && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-4" style={{ color: '#8a94ad' }}>
                    No pending posts
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                items.map((post) => {
                  const id = post.id || post.post_id;
                  return (
                    <tr key={id}>
                      <td className="small">{post.user_name || post.user_email || '-'}</td>
                      <td>
                        <div
                          className="text-truncate"
                          style={{ maxWidth: '300px' }}
                          title={post.content || post.body || ''}
                        >
                          {post.content || post.body || '-'}
                        </div>
                        {post.image_url && (
                          <a
                            href={post.image_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="small"
                          >
                            [Image]
                          </a>
                        )}
                      </td>
                      <td className="small">{post.store_name || '-'}</td>
                      <td className="text-nowrap small">{fmtDate(post.created_at)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleModerate(id, 'approved')}
                            disabled={moderating === id}
                            style={{ fontSize: '0.75rem' }}
                          >
                            Approve
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => {
                              const reason = window.prompt('Reject reason:');
                              if (reason !== null) {
                                handleModerate(id, 'rejected', reason);
                              }
                            }}
                            disabled={moderating === id}
                            style={{ fontSize: '0.75rem' }}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
        <div className="card-footer bg-white">
          <Pagination
            hasMore={hasMore}
            hasPrev={cursorHistory.length > 0}
            onNext={handleNext}
            onPrev={handlePrev}
            loading={loading}
          />
        </div>
      </div>
    </Layout>
  );
}
